import { DateTime } from 'luxon'
import { useCallback, useMemo, useState } from 'react'
import { Accordion, Button, Form } from 'react-bootstrap'
import { SALESFORCE_URLS_BY_ENVIRONMENT } from '../../../common/common.entities'
import { Listing__c_Missing, PicklistValueInfo } from '../../utilities/listings.interfaces'
import { CustomErrorProps } from '../../../common/components/CustomError'
import useCustomPagination from '../../../common/components/CustomPagination'

interface ListingsMissingViewProps {
  environment: string
  missing: Listing__c_Missing[]
  missingMarketingStatuses: string[]
  inventoryStatuses: PicklistValueInfo[]
  delistedReasons: PicklistValueInfo[]
  showListingsLookupButton: boolean
  setError: (content: CustomErrorProps) => void
  loadProductListings: (productId: string, mpn: string, integrationId?: string) => Promise<void>;
  loadListingAdjustments: (productId: string, mpn: string, integrationId?: string) => Promise<void>;
  loadListingOrderItems: (productId: string, mpn: string, integrationId?: string) => Promise<void>;
  loadListingInventory: (productId: string, mpn: string, integrationId?: string) => Promise<void>;
}

export const ListingsMissingView: React.FC<ListingsMissingViewProps> = ({
  environment,
  missing,
  missingMarketingStatuses,
  inventoryStatuses,
  delistedReasons,
  showListingsLookupButton,
  setError,
  loadProductListings,
  loadListingAdjustments,
  loadListingOrderItems,
  loadListingInventory
}) => {
  if(!missing) return null

  const [missingPageSize, setMissingPageSize] = useState(10)
  const [filteredMissing, setFilteredMissing]: [Listing__c_Missing[], any] = useState([])
  const [missingFilterText, setMissingFilterText]: [string, any] = useState(undefined)
  
  const {paginatedItems: missingPaginatedItems, paginationControls: missingPaginationControls} = useCustomPagination<Listing__c_Missing>({
    pageSize: missingPageSize,
    sourceItems: filteredMissing.length > 0 ? filteredMissing : missing,
  })
  
  const handleMissingFilterMpnChanged = useCallback((e) => {
    if(!e.target.value || e.target.value.trim() === ''){
      setFilteredMissing([])
      setMissingFilterText('')
      return
    }
    const filtered = missing.filter(l => l.temp.Product?.MPN__c?.toLowerCase().includes(e.target.value.toLowerCase()))
    setMissingFilterText(`${filtered.length} listing(s) filtered for product mpn of '${e.target.value}'.`)
    setFilteredMissing(filtered)
  }, [missing])
  
  const handleMissingListingInventoryClicked = useCallback(async (e) => {
    setError({visible: false})
    const id = e.currentTarget.dataset.recordid
    const integrationId = e.currentTarget.dataset.integrationid
    const listing = missing.find(l => l.Product__c === id)
    await loadListingInventory(listing.Product__c, listing.temp.Product.MPN__c, integrationId)
  }, [missing])

  const handleMissingListingOrderItemsClicked = useCallback(async (e) => {
    setError({visible: false})
    const id = e.currentTarget.dataset.recordid
    const integrationId = e.currentTarget.dataset.integrationid
    const listing = missing.find(l => l.Product__c === id)
    await loadListingOrderItems(listing.Product__c, listing.temp.Product.MPN__c, integrationId)
  }, [missing])

  const handleMissingListingAdjustmentsClicked = useCallback(async (e) => {
    setError({visible: false})
    const id = e.currentTarget.dataset.recordid
    const integrationId = e.currentTarget.dataset.integrationid
    const listing = missing.find(l => l.Product__c === id)
    await loadListingAdjustments(listing.Product__c, listing.temp.Product.MPN__c, integrationId)
  }, [missing])

  const handleMissingListingListingsClicked = useCallback(async (e) => {
    setError({visible: false})
    const id = e.currentTarget.dataset.recordid
    const integrationId = e.currentTarget.dataset.integrationid
    const listing = missing.find(l => l.Product__c === id)
    await loadProductListings(listing.Product__c, listing.temp.Product.MPN__c, integrationId)
  }, [missing])

  const handleFilterMissingMarketingStatusChanged = useCallback((e) => {
    if(e.target.value === 'none'){
      const filtered = missing.filter(l => l.temp.Product.Marketing_Status__c === null)
      setMissingFilterText(`${filtered.length} listing(s) filtered for unset marketing status.`)
      setFilteredMissing(filtered)
    } else if(e.target.value === 'any'){
      setFilteredMissing([])
      setMissingFilterText('')
    } else {
      const filtered = missing.filter(l => l.temp.Product.Marketing_Status__c === e.target.value)
      setMissingFilterText(`${filtered.length} listing(s) filtered for marketing status of '${e.target.value}'.`)
      setFilteredMissing(filtered)
    }
  }, [missing])

  const handleCreateAllListingsClicked = useCallback(async (e) => {
    if(!confirm('This will create all of the missing listings in Mission Control. Are you sure?')) return


  }, [])

  const missingView = useMemo(() => {
    return <div>
      <div style={{display: 'flex', flexWrap: 'wrap', gap: '15px'}}>
        <Button variant='outline-secondary' onClick={handleCreateAllListingsClicked}>CREATE ALL LISTINGS</Button>
      </div>
      <br/>
      <table className='flexport-table'>
        <thead className='sticky-row-2'>
          <tr>
            <td colSpan={50}>
              <Accordion className='custom-accordion'>
                <Accordion.Item eventKey='0'>
                  <Accordion.Header><span className='beacon-label'>FILTERS</span></Accordion.Header>
                  <Accordion.Body>
                    <div style={{display: 'flex', flexWrap: 'wrap', gap: '5px'}}>
                      <div style={{width: '5%'}}>
                        <span className='beacon-label'>PAGE SIZE</span>
                        <Form.Control type='number' value={missingPageSize} onChange={(e) => setMissingPageSize(+e.currentTarget.value <= 0 ? 1 : +e.currentTarget.value)} />
                      </div>
                      <div>
                        <span className='beacon-label'>MPN SEARCH</span>
                        <Form.Control
                          type='text'
                          placeholder='Enter an MPN...'
                          onChange={handleMissingFilterMpnChanged}
                        />
                      </div>
                      <div>
                        <span className='beacon-label'>MARKETING STATUS</span>
                        <Form.Select style={{height: '35px'}} onChange={handleFilterMissingMarketingStatusChanged}>
                          <option value='any'>ANY</option>
                          <option value='none'>NONE</option>
                          {missingMarketingStatuses.map(ms => (
                            <option value={ms}>{ms}</option>
                          ))}
                        </Form.Select>
                      </div>                      
                    </div>
                    <span className='beacon-label'>{missingFilterText}</span>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </td>
          </tr>
          <tr>
            <th>
              <span className='beacon-header'>NAME</span>
            </th>
            <th>
              <span className='beacon-header'>SKU</span>
            </th>
            <th>
              <span className='beacon-header'>INVENTORY STATUS</span>
            </th>
            <th colSpan={2}>
              <span className='beacon-header'>PRODUCT</span>
            </th>
            <th>
              <span className='beacon-header'>ACTIONS</span>
            </th>
            <th>
              <span className='beacon-header'>LOOKUPS</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {missingPaginatedItems.map((listing, index) => (
            <tr style={{borderLeft: '1px solid lightgrey'}}>
              {/* LISTING CELLS */}
              <td>
                <Form.Control 
                  type='text'
                  value={listing.Name}
                />
              </td>
              <td>
                <Form.Control 
                  type='text'
                  value={listing.SKU__c}
                />
              </td>
              <td>
                <Form.Select disabled={listing.Name.includes('-FBA_')} id={`inventory-status-${listing.Name}`} style={{height: '35px'}}>
                  <option id={`inventory-status-none-${listing.Name}`} selected={!listing.Inventory_Status__c}>-- NONE --</option>
                  {inventoryStatuses.map((is) => (
                    <option id={`inventory-status-${is.Value}-${listing.Name}`} selected={listing.Inventory_Status__c === is.Label} value={is.Value}>{is.Label}</option>
                  ))}
                </Form.Select>
              </td>
              {/* PRODUCT CELLS */}
              <td style={{borderLeft: '1px solid lightgrey'}}>
                <span title='Opens the Product in Mission Control' className='beacon-header'>
                  <a href={`${SALESFORCE_URLS_BY_ENVIRONMENT[environment]}/${listing.Product__c}`} className='beacon-link' target="_blank" rel="noopener noreferrer">
                    {listing.temp.Product.MPN__c}
                    <br/>
                    {listing.temp.Product.Image_URL_1__c != null && <img src={listing.temp.Product.Image_URL_1__c} style={{height: '75px', width: '75px'}}></img>}
                  </a>
                </span>
              </td>
              <td>
                <div style={{display: 'flex', flexWrap: 'wrap', gap: '5px'}}>
                  <div>
                    <table className='listings-inner-table'>
                      <tbody>
                        <tr>
                          <td style={{textAlign: 'right'}}>
                            <span className='beacon-label'>MPN</span>
                          </td>
                          <td style={{textAlign: 'left'}}>{listing.temp.Product.MPN__c}</td>
                        </tr>
                        <tr>
                          <td style={{textAlign: 'right'}}>
                            <span className='beacon-label'>SECONDARY SKU</span>
                          </td>
                          <td style={{textAlign: 'left'}}>
                            {listing.temp.Product.Secondary_SKU__c ?? 'N/A'}
                          </td>
                        </tr>
                        <tr>
                          <td style={{textAlign: 'right'}}>
                            <span className='beacon-label'>MARKETING STATUS</span>
                          </td>
                          <td style={{textAlign: 'left'}}>
                            {listing.temp.Product.Marketing_Status__c}
                          </td>
                        </tr>
                        <tr>
                          <td style={{textAlign: 'right'}}>
                            <span className='beacon-label'>UNAUTHORIZED COUNTRIES</span>
                          </td>
                          <td style={{textAlign: 'left'}}>
                            {listing.temp.Product.Unauthorized_Countries__c?.split(';').join(', ')}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div>
                    <table className='listings-inner-table'>
                      <tbody>
                        <tr>
                          <td>
                            <span className='beacon-label'>UPC</span>
                          </td>
                          <td>
                            {listing.temp.Product.UPC__c}
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <span className='beacon-label'>ASIN</span>
                          </td>
                          <td>
                            {listing.temp.Product.ASIN__c}
                          </td>
                        </tr>  
                        <tr>
                          <td>
                            <span className='beacon-label'>CREATED</span>
                          </td>
                          <td>
                            {DateTime.fromISO(listing.temp.Product.CreatedDate).toFormat('MM/dd/yy t')}
                          </td>
                        </tr>                      
                      </tbody>
                    </table>
                  </div>
                </div>
              </td>
              <td style={{borderLeft: '1px solid lightgrey'}}>
                <Button variant='outline-secondary'>CREATE LISTING</Button>
              </td>
              {/* LOOKUPS */}
              <td style={{borderRight: '1px solid lightgrey'}}>
                <Button style={{width: '95%'}} variant='secondary' data-integrationid={listing.Integration__c} data-recordid={listing.Product__c} onClick={handleMissingListingInventoryClicked}>INVENTORY</Button>
                <br/>
                <br/>
                <Button style={{width: '95%'}} variant='secondary' data-integrationid={listing.Integration__c} data-recordid={listing.Product__c} onClick={handleMissingListingOrderItemsClicked}>ORDER ITEMS</Button>
                <br/>
                <br/>
                <Button style={{width: '95%'}} variant='secondary' data-integrationid={listing.Integration__c} data-recordid={listing.Product__c} onClick={handleMissingListingAdjustmentsClicked}>ADJUSTMENTS</Button>
                {showListingsLookupButton === true && <>
                  <br/>
                  <br/>
                  <Button style={{width: '95%'}} variant='secondary' data-integrationid={listing.Integration__c} data-recordid={listing.Product__c} onClick={handleMissingListingListingsClicked}>LISTINGS</Button>
                </>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className='sticky-bottom-element'>{missingPaginationControls}</div>
    </div>
  }, [missingPaginatedItems, inventoryStatuses, delistedReasons, missingFilterText, missingMarketingStatuses])

  return missingView
}